import { ReactNode, useEffect, useRef, useState, forwardRef } from 'react';
import { cn } from '@/lib/utils';

interface ScrollRevealProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  duration?: number;
  threshold?: number;
}

export const ScrollReveal = forwardRef<HTMLDivElement, ScrollRevealProps>(
  ({ children, className, delay = 0, direction = 'up', duration = 600, threshold = 0.1 }, _ref) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      const element = ref.current;
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(element);
          }
        },
        { threshold, rootMargin: '0px 0px -30px 0px' }
      );

      observer.observe(element);
      return () => observer.disconnect();
    }, [threshold]);

    const getInitialTransform = () => {
      switch (direction) {
        case 'up': return 'translateY(24px)';
        case 'down': return 'translateY(-24px)';
        case 'left': return 'translateX(24px)';
        case 'right': return 'translateX(-24px)';
        case 'none': return 'none';
      }
    };

    return (
      <div
        ref={ref}
        className={cn(className)}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'none' : getInitialTransform(),
          transition: `opacity ${duration}ms ease-out, transform ${duration}ms ease-out`,
          transitionDelay: `${delay}ms`,
        }}
      >
        {children}
      </div>
    );
  }
);

ScrollReveal.displayName = 'ScrollReveal';
